import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, BarChart3, Zap, CheckCircle, ArrowRight, GitBranch, Users, Star } from 'lucide-react';
import logo from '../assets/images/repugate_logo.png';
import vercelLogo from '../assets/images/vercel_logo.png';
import supabaseLogo from '../assets/images/supabase_logo.png';
import clerkLogo from '../assets/images/clerk_logo.png';
import groqLogo from '../assets/images/groq_logo.png';

function LandingPage({ onGetStarted }) {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [githubData, setGithubData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Fetch GitHub user data
    const fetchGitHubData = async () => {
      try {
        const response = await fetch('https://api.github.com/users/Mostakim52');
        if (response.ok) {
          const data = await response.json();
          setGithubData(data);
        }
      } catch (err) {
        console.error('Failed to fetch GitHub data:', err);
      }
    };

    fetchGitHubData();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrolled = window.scrollY / scrollHeight;
      setScrollProgress(scrolled * 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.15, duration: 0.6 }
    })
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 overflow-x-hidden">
      {/* Scroll Progress Bar */}
      <div className="fixed top-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-500 to-pink-500 z-50" style={{ width: `${scrollProgress}%` }}></div>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-40 bg-black/30 backdrop-blur-md border-b border-purple-500/20">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={logo} alt="RepuGate Logo" className="h-10 w-auto" />
            <div className="flex flex-col">
              <span className="text-xl font-black bg-gradient-to-r from-purple-400 to-pink-400 text-transparent bg-clip-text">RepuGate</span>
              <span className="text-xs text-purple-300">Your Gateway to a Better Reputation</span>
            </div>
          </div>
          <button
            onClick={onGetStarted}
            className="px-6 py-2 rounded-lg font-semibold bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 transition-all"
          >
            Get Started
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="min-h-screen flex items-center justify-center px-6 pt-20 relative overflow-hidden">
        {/* Animated background circles */}
        <div className="absolute inset-0 overflow-hidden">
          <motion.div
            className="absolute w-96 h-96 bg-purple-500/20 rounded-full blur-3xl"
            style={{ top: '10%', left: '10%' }}
            animate={{ y: [0, 30, 0] }}
            transition={{ duration: 6, repeat: Infinity }}
          ></motion.div>
          <motion.div
            className="absolute w-96 h-96 bg-pink-500/20 rounded-full blur-3xl"
            style={{ bottom: '10%', right: '10%' }}
            animate={{ y: [0, -30, 0] }}
            transition={{ duration: 6, repeat: Infinity }}
          ></motion.div>
        </div>

        <motion.div
          className="max-w-4xl mx-auto text-center relative z-10"
          initial="hidden"
          animate="visible"
        >
          <motion.div custom={0} variants={fadeInUp} className="mb-6">
            <span className="text-sm font-semibold text-purple-300 bg-purple-500/20 px-4 py-1 rounded-full border border-purple-500/30">
              ✨ AI-Powered Review Management
            </span>
          </motion.div>

          <motion.h1 custom={1} variants={fadeInUp} className="text-6xl md:text-7xl font-black mb-4 leading-tight">
            <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-purple-400 text-transparent bg-clip-text">
              Win Every Review
            </span>
            <br />
            <span className="text-white">with AI Replies</span>
          </motion.h1>

          <motion.p custom={1.5} variants={fadeInUp} className="text-lg text-purple-300 mb-8 font-semibold italic">
            Your Gateway to a Better Reputation
          </motion.p>

          <motion.p custom={2} variants={fadeInUp} className="text-xl text-purple-200 mb-8 max-w-2xl mx-auto leading-relaxed">
            Stop losing customers to negative reviews. Use AI to generate thoughtful, professional replies in seconds. Manage all your Facebook reviews from one beautiful dashboard.
          </motion.p>

          <motion.button
            custom={3}
            variants={fadeInUp}
            onClick={onGetStarted}
            className="px-8 py-4 rounded-lg font-semibold bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 transition-all flex items-center gap-2 mx-auto shadow-lg hover:shadow-xl"
          >
            Get Started <ArrowRight size={20} />
          </motion.button>


        </motion.div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6 relative">
        <div className="max-w-6xl mx-auto">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true, margin: '-100px' }}
          >
            <h2 className="text-5xl font-black text-white mb-6">Why Businesses Love RepuGate</h2>
            <p className="text-xl text-purple-300">Everything you need to master review management</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Zap,
                title: 'Lightning Fast',
                desc: 'Generate AI replies in seconds, not hours'
              },
              {
                icon: MessageSquare,
                title: 'Multi-Language',
                desc: 'Reply in English or Bangla automatically'
              },
              {
                icon: BarChart3,
                title: 'Boost Reputation',
                desc: 'Professional replies show customers you care'
              }
            ].map((feature, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1, duration: 0.6 }}
                viewport={{ once: true, margin: '-100px' }}
                className="p-8 rounded-2xl bg-slate-800/50 border border-purple-500/30 hover:border-purple-500/60 transition-all group hover:bg-slate-800/80"
              >
                <feature.icon className="w-12 h-12 text-pink-400 mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-2xl font-bold text-white mb-3">{feature.title}</h3>
                <p className="text-purple-200">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <motion.h2
            className="text-5xl font-black text-white mb-12 text-center"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true, margin: '-100px' }}
          >
            How It Works
          </motion.h2>

          <div className="space-y-12">
            {[
              { num: '1', title: 'Connect Facebook', desc: 'Link your Facebook page with one click' },
              { num: '2', title: 'Load Reviews', desc: 'See all your recent reviews in one place' },
              { num: '3', title: 'Generate Replies', desc: 'Let AI create 3 reply options in your language' },
              { num: '4', title: 'Copy & Paste', desc: 'Copy the reply and post it directly on Facebook' }
            ].map((step, i) => (
              <motion.div
                key={i}
                className="flex gap-6 items-start"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.1, duration: 0.6 }}
                viewport={{ once: true, margin: '-100px' }}
              >
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-12 w-12 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold text-lg">
                    {step.num}
                  </div>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white mb-2">{step.title}</h3>
                  <p className="text-purple-300 text-lg">{step.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>



      {/* Benefits List */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            className="bg-gradient-to-br from-purple-900/50 to-pink-900/50 border border-purple-500/30 rounded-3xl p-12"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true, margin: '-100px' }}
          >
            <h2 className="text-4xl font-black text-white mb-8">Complete Peace of Mind</h2>

            <div className="space-y-4">
              {[
                'Save 10+ hours per week',
                'Respond to 100% of reviews',
                'AI-powered, human-quality replies',
                'Multi-language support',
                'All in one beautiful dashboard',
                'Works with Facebook pages'
              ].map((benefit, i) => (
                <motion.div
                  key={i}
                  className="flex items-center gap-4"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05, duration: 0.5 }}
                  viewport={{ once: true, margin: '-100px' }}
                >
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0" />
                  <span className="text-lg text-purple-100">{benefit}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Built With & Credits Section */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true, margin: '-100px' }}
          >
            <h2 className="text-4xl font-black text-white mb-4">Built With Modern Technology</h2>
            <p className="text-lg text-purple-300">Powered by leading platforms and services</p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 items-center">
            {/* Services Grid */}
            <motion.div
              className="grid grid-cols-1 sm:grid-cols-2 gap-6"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true, margin: '-100px' }}
            >
              {/* Vercel */}
              <div className="p-8 rounded-2xl bg-white flex flex-col items-center justify-center hover:shadow-lg transition-shadow">
                <img src={vercelLogo} alt="Vercel" className="h-12 w-auto mb-2" />
                <p className="font-bold text-black text-sm">Vercel</p>
                <p className="text-gray-600 text-xs mt-1">Deployment</p>
              </div>

              {/* Supabase */}
              <div className="p-8 rounded-2xl bg-white flex flex-col items-center justify-center hover:shadow-lg transition-shadow border border-gray-200">
                <img src={supabaseLogo} alt="Supabase" className="h-12 w-auto mb-2" />
                <p className="font-bold text-gray-900 text-sm">Supabase</p>
                <p className="text-gray-600 text-xs mt-1">Database</p>
              </div>

              {/* Clerk */}
              <div className="p-8 rounded-2xl bg-white flex flex-col items-center justify-center hover:shadow-lg transition-shadow border border-gray-200">
                <img src={clerkLogo} alt="Clerk" className="h-12 w-auto mb-2" />
                <p className="font-bold text-gray-900 text-sm">Clerk</p>
                <p className="text-gray-600 text-xs mt-1">Authentication</p>
              </div>

              {/* Groq AI */}
              <div className="p-8 rounded-2xl bg-white flex flex-col items-center justify-center hover:shadow-lg transition-shadow border border-gray-200">
                <img src={groqLogo} alt="Groq" className="h-12 w-auto mb-2" />
                <p className="font-bold text-gray-900 text-sm">Groq</p>
                <p className="text-gray-600 text-xs mt-1">AI Engine</p>
              </div>
            </motion.div>

            {/* GitHub Profile Widget */}
            {githubData && (
              <motion.div
                className="bg-gradient-to-br from-slate-800 to-slate-900 border border-purple-500/30 rounded-2xl p-8 text-center hover:border-purple-500/60 transition-all"
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true, margin: '-100px' }}
              >
                <div className="mb-6">
                  <img
                    src={githubData.avatar_url}
                    alt="GitHub Profile"
                    className="w-24 h-24 rounded-full mx-auto border-4 border-purple-500/50 mb-4"
                  />
                  <h3 className="text-2xl font-black text-white">{githubData.name || githubData.login}</h3>
                  <p className="text-purple-300 text-sm mt-2">{githubData.bio}</p>
                </div>

                <div className="grid grid-cols-3 gap-4 mb-6 py-6 border-y border-purple-500/20">
                  <div className="flex flex-col items-center">
                    <p className="text-3xl font-black text-pink-400">{githubData.public_repos}</p>
                    <p className="text-xs text-purple-300 mt-1 flex items-center justify-center gap-1">
                      <GitBranch size={14} /> Repositories
                    </p>
                  </div>
                  <div className="flex flex-col items-center">
                    <p className="text-3xl font-black text-pink-400">{githubData.followers}</p>
                    <p className="text-xs text-purple-300 mt-1 flex items-center justify-center gap-1">
                      <Users size={14} /> Followers
                    </p>
                  </div>
                  <div className="flex flex-col items-center">
                    <p className="text-3xl font-black text-pink-400">{githubData.following}</p>
                    <p className="text-xs text-purple-300 mt-1 flex items-center justify-center gap-1">
                      <Star size={14} /> Following
                    </p>
                  </div>
                </div>

                <a
                  href="https://github.com/Mostakim52"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 transition-all"
                >
                  🔗 Visit GitHub Profile
                </a>
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* CTA Footer */}
      <section className="py-20 px-6">
        <motion.div
          className="max-w-4xl mx-auto text-center bg-gradient-to-r from-purple-600 to-pink-600 rounded-3xl p-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true, margin: '-100px' }}
        >
          <h2 className="text-4xl font-black text-white mb-6">Ready to Transform Your Reviews?</h2>
          <p className="text-lg text-purple-100 mb-8">Start managing your Facebook reviews with AI-powered replies today.</p>
          <button
            onClick={onGetStarted}
            className="px-8 py-4 rounded-lg font-semibold bg-white text-purple-600 hover:bg-gray-100 transition-all inline-flex items-center gap-2 shadow-lg"
          >
            Get Started <ArrowRight size={20} />
          </button>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="border-t border-purple-500/20 py-12 px-6 mt-20">
        <div className="max-w-6xl mx-auto">
          <div className="text-center text-purple-300 mb-6">
            <p>&copy; 2026 RepuGate. All rights reserved.</p>
          </div>
          <div className="flex justify-center gap-6">
            <button
              onClick={() => navigate('/privacy')}
              className="text-purple-400 hover:text-purple-300 transition-colors text-sm font-medium"
            >
              Privacy Policy
            </button>
            <span className="text-purple-700">•</span>
            <a
              href="mailto:privacy@repugate.io"
              className="text-purple-400 hover:text-purple-300 transition-colors text-sm font-medium"
            >
              Contact Us
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
